import React, { useId, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Stethoscope, BadgeCheck, Wallet, CalendarClock, ShieldCheck,
  Smartphone, ChevronDown, GraduationCap,
} from 'lucide-react';
import useSeoMeta from '../hooks/useSeoMeta';
import useFaqJsonLd from '../hooks/useFaqJsonLd';
import usePricing from '../hooks/usePricing';
import { describeDoctorEarnings } from '../config/pricing';

// Recibe la tarifa del backend; con pricing null la respuesta de ingresos queda
// neutra (sin cifras).
const buildDoctorQuestions = (pricing) => [
  {
    icon: BadgeCheck,
    q: '¿Quién puede registrarse como médico?',
    a: 'Cualquier médico general colombiano con título profesional, registro médico y matrícula profesional vigente. DocEnLinea verifica estos documentos antes de habilitar tu perfil.',
  },
  {
    icon: Wallet,
    q: '¿Cuánto gano por consulta?',
    a: `Recibes el valor que paga el paciente menos una comisión fija por el uso de la plataforma. ${describeDoctorEarnings(pricing)}`,
  },
  {
    icon: CalendarClock,
    q: '¿Cómo manejo mi agenda?',
    a: 'Tu configuras tu disponibilidad por día y franja horaria desde tu panel. Los pacientes solo pueden agendar en los horarios que tú habilitas.',
  },
  {
    icon: Smartphone,
    q: '¿Cómo atiendo las consultas?',
    a: 'Atiendes por videollamada o chat desde tu celular o computador, sin instalar aplicaciones adicionales. Recibes notificación cuando un paciente agenda contigo.',
  },
  {
    icon: GraduationCap,
    q: '¿Puedo generar fórmulas y registrar historia clínica?',
    a: 'Sí. La plataforma te permite generar fórmula médica digital y registrar la historia clínica de cada atención conforme a la normatividad colombiana vigente.',
  },
  {
    icon: ShieldCheck,
    q: '¿Qué responsabilidad tiene DocEnLinea?',
    a: 'DocEnLinea es un intermediario tecnológico. Eres un profesional independiente y asumes la responsabilidad total del acto médico, el diagnóstico y el tratamiento de tus pacientes.',
  },
];

function FaqRow({ item, isOpen, onToggle }) {
  const Icon = item.icon;
  const panelId = useId();
  return (
    <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-gray-50 transition-colors"
        aria-expanded={isOpen}
        aria-controls={panelId}
      >
        <span className="flex items-center gap-3">
          <Icon className="h-5 w-5 text-primary-600 flex-shrink-0" />
          <span className="font-semibold text-gray-900">{item.q}</span>
        </span>
        <ChevronDown
          className={`h-5 w-5 text-primary-600 flex-shrink-0 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      {/* La respuesta se renderiza siempre y se oculta con CSS. Si se
          desmontara, el texto no existiría en el DOM y los buscadores no
          podrían indexarlo. */}
      <div
        id={panelId}
        className={`px-5 pb-5 pl-14 text-gray-600 whitespace-pre-line ${isOpen ? '' : 'hidden'}`}
      >
        {item.a}
      </div>
    </div>
  );
}

function LandingDoctors() {
  const [openIndex, setOpenIndex] = useState(0);

  useSeoMeta(
    'Trabaja como médico online en Colombia | DocEnLinea',
    'Únete a DocEnLinea como médico general independiente: atiende por videollamada o chat, maneja tu propia agenda y recibe tus pagos directo a Nequi.',
    '/medicos'
  );
  const { pricing } = usePricing();
  const doctorQuestions = useMemo(() => buildDoctorQuestions(pricing), [pricing]);
  useFaqJsonLd(doctorQuestions);

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-50 to-white">
      {/* Navbar (idéntico a HomePage) */}
      <nav className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <Link to="/" className="flex items-center">
              <Stethoscope className="h-8 w-8 text-primary-600" />
              <span className="ml-2 text-xl font-bold text-gray-900">DocEnLinea</span>
            </Link>
            <div className="hidden md:flex items-center gap-6 mr-4">
              <Link to="/pacientes" className="text-gray-600 hover:text-primary-600 text-sm font-medium">Para pacientes</Link>
              <Link to="/medicos" className="text-primary-600 text-sm font-semibold">Para médicos</Link>
              <Link to="/faq" className="text-gray-600 hover:text-primary-600 text-sm font-medium">Preguntas frecuentes</Link>
              <Link to="/soporte" className="text-gray-600 hover:text-primary-600 text-sm font-medium">Soporte</Link>
            </div>
            <div className="flex gap-4">
              <Link to="/login" className="btn-secondary">Iniciar Sesión</Link>
              <Link to="/register" className="btn-primary">Registrarse</Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="text-center">
          <span className="inline-flex items-center gap-2 bg-primary-100 text-primary-700 text-sm font-medium px-4 py-1.5 rounded-full mb-6">
            <BadgeCheck className="h-4 w-4" /> Para médicos generales colombianos
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            Atiende pacientes online
            <br />
            <span className="text-primary-600">con la agenda y los ingresos bajo tu control</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Únete a DocEnLinea como médico independiente: define tus horarios, atiende por
            videollamada o chat y recibe tus pagos directamente a tu cuenta Nequi.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/register" className="btn-primary text-lg px-8 py-3">
              Registrarme como médico
            </Link>
            <a href="#preguntas-medico" className="btn-secondary text-lg px-8 py-3">
              Ver preguntas frecuentes
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        {/* Preguntas frecuentes clave (SEO) */}
        <section id="preguntas-medico" className="scroll-mt-20">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-3">
            Lo que todo médico debe saber antes de unirse
          </h2>
          <p className="text-center text-gray-600 mb-10 max-w-2xl mx-auto">
            Respondemos las dudas más comunes sobre cómo funciona DocEnLinea para
            profesionales de la salud.
          </p>
          <div className="space-y-3">
            {doctorQuestions.map((item, i) => (
              <FaqRow
                key={item.q}
                item={item}
                isOpen={openIndex === i}
                onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
              />
            ))}
          </div>
        </section>

        {/* CTA intermedio */}
        <section className="mt-20 bg-primary-600 rounded-2xl p-10 sm:p-12 text-white text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">
            Empieza a atender pacientes en pocos días
          </h2>
          <p className="text-primary-100 mb-8 max-w-xl mx-auto">
            Regístrate, sube tus documentos y espera la verificación de tu perfil. Una vez
            aprobado, configura tu disponibilidad y empieza a recibir citas.
          </p>
          <Link
            to="/register"
            className="bg-white text-primary-600 px-8 py-3 rounded-lg font-medium hover:bg-primary-50 transition-colors inline-block"
          >
            Crear mi cuenta de médico
          </Link>
        </section>

        {/* Enlace cruzado a landing de pacientes */}
        <section className="mt-16 text-center">
          <p className="text-gray-600">
            ¿Eres paciente y quieres saber cómo funciona tu consulta?{' '}
            <Link to="/pacientes" className="text-primary-600 font-medium hover:underline">
              Ver información para pacientes
            </Link>
          </p>
        </section>
      </main>

      {/* Footer (idéntico al resto del sitio) */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center mb-4">
            <Stethoscope className="h-8 w-8" />
            <span className="ml-2 text-xl font-bold">DocEnLinea</span>
          </div>
          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-400 mb-4">
            <Link to="/" className="hover:text-white">Inicio</Link>
            <Link to="/pacientes" className="hover:text-white">Para pacientes</Link>
            <Link to="/medicos" className="hover:text-white">Para médicos</Link>
            <Link to="/faq" className="hover:text-white">Preguntas Frecuentes</Link>
            <Link to="/soporte" className="hover:text-white">Soporte</Link>
            <Link to="/login" className="hover:text-white">Iniciar Sesión</Link>
            <Link to="/register" className="hover:text-white">Registrarse</Link>
          </div>
          <p className="text-gray-500 text-xs mt-4 max-w-xl mx-auto">
            DocEnLinea NO es un servicio de emergencias. Ante una urgencia vital, llama al 123
            o acude al servicio de urgencias más cercano.
          </p>
          <p className="text-gray-500 text-sm mt-4">
            © 2025 DocEnLinea. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default LandingDoctors;
